export type ImportedQuestion = {
  numeroOriginal: number;
  banca: string;
  ano: string;
  area: string;
  disciplina: string;
  assunto: string;
  dificuldade: string;
  tipo: string;
  texto: string;
  alts: string[];
  correta: string;
  gabarito: string;
  anulada?: boolean;
  comentario: string;
};

export type AnswerEntry = { numero: number; resposta: string; comentario: string };

const clean = (value: unknown) => String(value ?? '').replace(/\u00a0/g, ' ').replace(/[ \t]+/g, ' ').trim();

function areaFor(number: number) {
  if (number <= 45) return 'Linguagens, Códigos e suas Tecnologias';
  if (number <= 90) return 'Ciências Humanas e suas Tecnologias';
  if (number <= 135) return 'Ciências da Natureza e suas Tecnologias';
  return 'Matemática e suas Tecnologias';
}

function disciplineFor(number: number) {
  if (number <= 45) return 'Linguagens';
  if (number <= 90) return 'Ciências Humanas';
  if (number <= 135) return 'Ciências da Natureza';
  return 'Matemática';
}

export function parseQuestions(text: string, year: string, difficulty = 'Média'): ImportedQuestion[] {
  const raw = String(text ?? '').replace(/\r/g, '');
  const heading = /^\s*Quest(?:ão|ao)?\s*(\d{1,3})\s*$/gim;
  const markers = [...raw.matchAll(heading)];
  return markers.map((match, index) => {
    const numero = Number(match[1]);
    const start = (match.index ?? 0) + match[0].length;
    const end = index + 1 < markers.length ? (markers[index + 1].index ?? raw.length) : raw.length;
    const block = raw.slice(start, end).trim();
    const altPattern = /^\s*([A-E])(?:[\)\].:\-]|\s)\s*(.+)$/gim;
    const alternatives = [...block.matchAll(altPattern)];
    const firstAlternative = alternatives[0]?.index ?? block.length;
    const statement = clean(block.slice(0, firstAlternative)) || `Questão ${numero}`;
    const byLetter: Record<string,string> = {};
    alternatives.forEach((alternative, altIndex) => {
      const letter = alternative[1].toUpperCase();
      const contentStart = (alternative.index ?? 0) + alternative[0].indexOf(alternative[2]);
      const contentEnd = altIndex + 1 < alternatives.length ? (alternatives[altIndex + 1].index ?? block.length) : block.length;
      byLetter[letter] = clean(block.slice(contentStart, contentEnd));
    });
    return {
      numeroOriginal: numero,
      banca: 'ENEM', ano: year, area: areaFor(numero), disciplina: disciplineFor(numero),
      assunto: `ENEM ${year} - Caderno Azul`, dificuldade: difficulty || 'Média',
      tipo: 'Múltipla Escolha', texto: statement,
      alts: ['A','B','C','D','E'].map(letter => byLetter[letter] || '(alternativa presente na imagem da prova)'),
      correta: '', gabarito: '', comentario: 'Importada do arquivo TXT com gabarito conferido.'
    };
  }).sort((a,b) => a.numeroOriginal - b.numeroOriginal);
}

export function parseAnswers(text: string): AnswerEntry[] {
  const map = new Map<number, AnswerEntry>();
  String(text ?? '').replace(/\r/g,'').split('\n').forEach(line => {
    const match = line.match(/^\s*(?:QUEST(?:Ã|A)O\s*)?(\d{1,3})\s*(?:[:;,\.\-–—]|\s)\s*(ANULAD[AO]|[A-E])\b\s*(.*)$/i);
    if (!match) return;
    const numero = Number(match[1]);
    const rawAnswer = match[2].toUpperCase();
    const resposta = rawAnswer.startsWith('ANULAD') ? 'Anulado' : rawAnswer;
    map.set(numero, {numero, resposta, comentario: clean(match[3])});
  });
  return [...map.values()].sort((a,b) => a.numero - b.numero);
}

export function applyAnswers(questions: ImportedQuestion[], answers: AnswerEntry[]) {
  const map = new Map(answers.map(answer => [answer.numero,answer]));
  let applied = 0;
  questions.forEach(question => {
    const answer = map.get(question.numeroOriginal);
    if (!answer) return;
    question.correta = answer.resposta;
    question.gabarito = answer.resposta;
    question.anulada = answer.resposta === 'Anulado';
    if (answer.comentario) question.comentario = answer.comentario;
    applied += 1;
  });
  return applied;
}
